import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

const TOKEN_KEY = "adybo_access_token";
const REFRESH_KEY = "adybo_refresh_token";

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(t) {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch {}
}
export function getRefreshToken() {
  try { return localStorage.getItem(REFRESH_KEY); } catch { return null; }
}
export function setRefreshToken(t) {
  try { if (t) localStorage.setItem(REFRESH_KEY, t); else localStorage.removeItem(REFRESH_KEY); } catch {}
}

// Decode JWT payload (no verify) — used only for reading exp timestamp
export function decodeJwt(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "===".slice((b64.length + 3) % 4);
    return JSON.parse(atob(padded));
  } catch { return null; }
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// --- Silent refresh con coda per evitare refresh concorrenti ---
let refreshInFlight = null;

async function performRefresh() {
  const rt = getRefreshToken();
  if (!rt) {
    // Nessun refresh token disponibile → fallisci subito senza chiamare il backend
    throw new Error("No refresh token");
  }
  const { data } = await axios.post(
    `${API}/auth/refresh`,
    { refresh_token: rt },
    {
      withCredentials: true,
      headers: { "X-Refresh-Token": rt, "Content-Type": "application/json" },
    },
  );
  if (data.access_token) setToken(data.access_token);
  if (data.refresh_token) setRefreshToken(data.refresh_token);
  return data;
}

export async function refreshAccessToken() {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function isAuthUrl(url = "") {
  return (
    url.includes("/auth/login") ||
    url.includes("/auth/refresh") ||
    url.includes("/auth/logout")
  );
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config || {};
    const url = original.url || "";
    const status = err.response?.status;
    const detail = err.response?.data?.detail;
    // Backend segnala "sessione terminata da altro dispositivo" con un detail
    // strutturato → non tentare refresh, notifica direttamente il livello app.
    const isSessionTerminated =
      status === 401 && detail && typeof detail === "object" && detail.code === "session_terminated";

    if (isSessionTerminated) {
      try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
      } catch {}
      try {
        window.dispatchEvent(
          new CustomEvent("adybo:auth-expired", { detail: { reason: "session_terminated" } }),
        );
      } catch {}
      err._handled = true;
      return Promise.reject(err);
    }

    // 401 → prova refresh una volta e riesegui la request originale
    if (status === 401 && !isAuthUrl(url) && !original._retry) {
      original._retry = true;
      try {
        await refreshAccessToken();
        // Aggiorna header Authorization con nuovo token
        const t = getToken();
        if (t) {
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${t}`;
        }
        return api(original);
      } catch (e) {
        // Refresh fallito → pulizia sessione
        try {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_KEY);
        } catch {}
        // Se il refresh è fallito perché la sessione è stata terminata da un
        // altro dispositivo, propaga il motivo al livello app.
        const refreshDetail = e?.response?.data?.detail;
        const refreshReason =
          refreshDetail && typeof refreshDetail === "object" && refreshDetail.code === "session_terminated"
            ? "session_terminated"
            : "expired";
        try {
          window.dispatchEvent(
            new CustomEvent("adybo:auth-expired", { detail: { reason: refreshReason } }),
          );
        } catch {}
        err._handled = true;
        return Promise.reject(err);
      }
    }

    return Promise.reject(err);
  },
);

export default api;

export function formatApiError(detail) {
  if (detail == null) return "Errore imprevisto";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
